<template>
  <div>
    <div class="heading-5 font-weight-medium">Create New Gnosis Safe</div>
    <div class="mt-8">
      <div class="d-flex align-center">
        <div class="p-14-r gray--text">01</div>
        <div class="h1-26-m ml-5">Assign Owners</div>
      </div>
      <p class="p-14-r darkgray--text mt-3">
        Your safe will have one or more owners. Add additional owners and
        specify how many of them have to confirm a transaction before it gets
        executed.
      </p>
    </div>
    <form class="mt-3" autocomplete="off" @submit.prevent>
      <ValidationObserver ref="observer" aria-autocomplete="off">
        <div
          class="d-flex align-center"
          v-for="(owner, idx) in owners"
          :key="idx"
        >
          <div style="width:150px">
            <div class="p-12-r gray--text ml-2">Owner {{ idx + 1 }}</div>
            <ValidationProvider
              v-slot="{ errors }"
              name="Owner name"
              rules="required"
            >
              <v-text-field
                id="firstName"
                v-model="owner.name"
                placeholder="Owner Name"
                outlined
                class="c-input mt-2"
                :error-messages="errors"
              ></v-text-field>
            </ValidationProvider>
          </div>
          <div class="ml-3 flex-1">
            <div class="p-12-r gray--text ml-2">Owner Address</div>
            <ValidationProvider
              v-slot="{ errors }"
              name="Owner Address"
              :rules="{ required: true, ethAddress2: { index: idx, owners } }"
            >
              <v-text-field
                id="firstName"
                v-model="owner.address"
                placeholder="Owner Address"
                outlined
                class="c-input mt-2"
                :error-messages="errors"
              ></v-text-field>
            </ValidationProvider>
          </div>
          <v-icon
            class="icongray--text ml-3"
            @click="removeOwner(idx)"
            :style="{ visibility: idx === 0 ? 'hidden' : 'visible' }"
            >fas fa-trash</v-icon
          >
        </div>
      </ValidationObserver>
      <div class="text-right">
        <span class="p-16-r clickable" @click="addOwner">
          + Add another owner</span
        >
      </div>
    </form>
    <div class="mt-5">
      <div class="d-flex align-center">
        <div class="p-14-r gray--text">02</div>
        <div class="h1-26-m ml-5">Multisig Threshold</div>
      </div>
      <div class="mt-3">
        <div class="p-12-r gray--text ml-2">
          Any transaction requires the authorization of:
        </div>
        <div class="d-flex align-center mt-2">
          <div style="width:150px">
            <v-select
              class="c-input"
              outlined
              hide-details
              v-model="threshold"
              :items="thresholdItems"
              :clearable="false"
            ></v-select>
          </div>
          <div class="ml-3 p-16-r flex-1">
            out of {{ maxThreshold }} owners.
          </div>
        </div>
      </div>
    </div>
    <div class="mt-10 d-flex align-center justify-space-between">
      <div class="p-14-r darkgray--text clickable" @click="back">Back</div>
      <div class="d-flex justify-end align-center">
        <div class="p-16-m gray--text clickable" @click="cancel">Cancel</div>
        <c-btn @click="nextStep" class="ml-5">Continue</c-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { List } from "immutable";
import { required } from "vee-validate/dist/rules";
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode,
} from "vee-validate";
import web3 from "web3";

setInteractionMode("eager");

extend("required", {
  ...required,
  message: "Required.",
});
extend("ethAddress2", {
  params: ["index", "owners"],
  validate: (value, { index, owners }) => {
    const isValid = web3.utils.isAddress(value);
    if (!isValid) return "Must be a valid Ethereum address.";
    if (
      List(owners)
        .delete(index)
        .some(
          owner =>
            String(owner.address).toLowerCase() === String(value).toLowerCase()
        )
    ) {
      return "Address already introduced";
    }
    return true;
  },
});

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: ["safeInfo"],
  computed: {
    maxThreshold() {
      return this.owners.filter(owner => web3.utils.isAddress(owner.address))
        .length;
    },
    thresholdItems() {
      return Array(this.maxThreshold || 1)
        .fill(0)
        .map((value, index) => index + 1);
    },
  },
  data: () => ({
    threshold: 1,
    owners: [
      {
        name: null,
        address: null,
      },
    ],
  }),
  methods: {
    addOwner() {
      this.owners = List(this.owners)
        .push({ name: null, address: null })
        .toArray();
    },
    removeOwner(idx) {
      this.owners = List(this.owners)
        .delete(idx)
        .toArray();
    },
    back() {
      this.$emit("setStep", "1");
    },
    cancel() {
      this.$store.dispatch("closeAuthModal");
    },
    async nextStep() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      this.$emit("setSafeInfo", {
        safeAddress: null,
        owners: this.owners,
        threshold: this.threshold,
      });
      this.$emit("setStep", 3);
    },
  },
  mounted() {
    if (this.safeInfo.owners) {
      this.owners = this.safeInfo.owners;
    }
    if (this.safeInfo.threshold) {
      this.threshold = this.safeInfo.threshold;
    }
  },
  watch: {
    "thresholdItems.length"(value) {
      if (value < this.threshold) {
        this.threshold = value;
      }
    },
  },
};
</script>
